/**
 * Collect variant + custom field context at AI upload time.
 */
(function () {
  "use strict";

  function findProductContainer(scope) {
    const root =
      scope instanceof Element
        ? scope
        : scope instanceof Document
          ? scope.documentElement
          : document;

    if (root.matches?.(".ai-upload-section")) {
      return (
        root.closest(".product-information, .product-details, quick-add-dialog") ||
        root
      );
    }

    return (
      root.closest?.(".product-information, .product-details, quick-add-dialog") ||
      root.querySelector?.(
        ".product-information, .product-details, quick-add-dialog"
      ) ||
      root
    );
  }

  function getUploadSection(scope) {
    if (scope instanceof Element && scope.matches(".ai-upload-section")) {
      return scope;
    }

    const container = findProductContainer(scope);
    return container.querySelector?.(".ai-upload-section");
  }

  function decodeBase64Utf8(encoded) {
    if (!encoded) return "";
    try {
      const binary = atob(encoded);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch {
      return "";
    }
  }

  function normalizeProductConfig(raw) {
    if (!raw || typeof raw !== "object") return {};
    return {
      shopDomain: String(raw.d || ""),
      productId: raw.p != null ? raw.p : "",
      styleDescription: String(raw.sd || ""),
      productOptions: Array.isArray(raw.o) ? raw.o.filter(Boolean) : [],
      referenceImageUrls: Array.isArray(raw.ri) ? raw.ri.filter(Boolean) : [],
    };
  }

  function readProductConfigFromSection(section) {
    if (!section?.dataset?.vpx) return {};
    try {
      const json = decodeBase64Utf8(section.dataset.vpx);
      return normalizeProductConfig(JSON.parse(json));
    } catch {
      return {};
    }
  }

  window.getProductGenerationConfig = function (scope) {
    return readProductConfigFromSection(getUploadSection(scope));
  };

  function getFormForUpload(scope) {
    const uploadSection = getUploadSection(scope);
    const container = findProductContainer(scope);

    if (uploadSection?.dataset.sectionId) {
      const bySection = document.getElementById(
        "BuyButtons-ProductForm-" + uploadSection.dataset.sectionId
      );
      if (bySection) return bySection;
    }

    const hidden = uploadSection?.querySelector("#ai-custom-image-url");
    const linkedId = hidden?.getAttribute("form");
    if (linkedId) {
      const linked = document.getElementById(linkedId);
      if (linked) return linked;
    }

    return (
      container.querySelector?.('form[id*="ProductForm"]') ||
      uploadSection?.closest('form[id*="ProductForm"]')
    );
  }

  function normalizeOptionName(name) {
    return String(name || "")
      .trim()
      .replace(/:+\s*$/u, "");
  }

  function getSelectedVariant(variantPicker) {
    const variantScript = variantPicker?.querySelector(
      'script[type="application/json"]'
    );
    if (variantScript?.textContent) {
      try {
        return JSON.parse(variantScript.textContent);
      } catch {
        // fall through
      }
    }

    return null;
  }

  /**
   * Build variant options from Shopify data — not from visible legend text.
   *
   * @param {Document|Element|null|undefined} scope
   * @param {Record<string, unknown>|null} variant
   * @returns {{ name: string, value: string }[]}
   */
  function collectVariantOptionsFromData(scope, variant) {
    /** @type {{ name: string, value: string }[]} */
    const options = [];
    if (!variant) return options;

    const optionNames = getProductOptionNames(scope);
    const container = findProductContainer(scope || document);
    const variantPicker = container.querySelector("variant-picker");

    ["option1", "option2", "option3"].forEach((key, index) => {
      const value = variant[key];
      if (value == null || String(value).trim() === "") return;

      const name =
        optionNames[index] ||
        inferOptionNameFromPicker(variantPicker, index) ||
        `选项${index + 1}`;

      options.push({
        name: normalizeOptionName(name),
        value: String(value).trim(),
      });
    });

    return options;
  }

  /**
   * Fallback option names from form control attributes (not legend labels).
   *
   * @param {Element|null|undefined} variantPicker
   * @param {number} index
   */
  function inferOptionNameFromPicker(variantPicker, index) {
    if (!variantPicker) return "";

    const dropdowns = variantPicker.querySelectorAll(
      ".variant-option--dropdowns"
    );
    const dropdown = dropdowns[index];
    if (dropdown) {
      const select = dropdown.querySelector("select.variant-option__select");
      if (select instanceof HTMLSelectElement) {
        const match = select.name.match(/^options\[(.+)]$/);
        if (match?.[1]) {
          return match[1];
        }
      }
    }

    const fieldsets = variantPicker.querySelectorAll(
      "fieldset.variant-option--buttons"
    );
    const fieldset = fieldsets[index];
    if (fieldset) {
      const radio = fieldset.querySelector('input[type="radio"]');
      if (radio instanceof HTMLInputElement && radio.name) {
        const productId = variantPicker.dataset.productId || "";
        if (productId && radio.name.endsWith(`-${productId}`)) {
          const withoutProductId = radio.name.slice(
            0,
            -(productId.length + 1)
          );
          const separator = withoutProductId.lastIndexOf("-");
          if (separator > 0) {
            return withoutProductId.slice(0, separator);
          }
        }
      }
    }

    return "";
  }

  function collectCustomProperties(scope) {
    if (typeof window.ProductCustomFields?.getValues === "function") {
      return window.ProductCustomFields.getValues(scope);
    }

    const container = findProductContainer(scope);
    const component = container.querySelector("product-custom-fields-component");
    if (!component) return {};

    /** @type {Record<string, string>} */
    const values = {};

    component
      .querySelectorAll(".custom-field[data-field-heading]")
      .forEach((fieldEl) => {
        if (!(fieldEl instanceof HTMLElement)) return;

        const heading = fieldEl.getAttribute("data-field-heading");
        if (!heading) return;

        const fieldType = fieldEl.getAttribute("data-field-type");
        let value = "";

        if (fieldType === "text") {
          const input = fieldEl.querySelector("[data-custom-field-text]");
          value = input instanceof HTMLInputElement ? input.value.trim() : "";
        } else if (fieldType === "select") {
          const select = fieldEl.querySelector("[data-custom-field-select]");
          value =
            select instanceof HTMLSelectElement ? select.value.trim() : "";
        } else if (fieldType === "select_with_custom") {
          const select = fieldEl.querySelector("[data-custom-field-select]");
          if (select instanceof HTMLSelectElement) {
            const customValue =
              select.getAttribute("data-custom-value") || "__custom__";
            if (select.value === customValue) {
              const customInput = fieldEl.querySelector("[data-custom-text]");
              value =
                customInput instanceof HTMLInputElement
                  ? customInput.value.trim()
                  : "";
            } else {
              value = select.value.trim();
            }
          }
        } else if (fieldType === "color") {
          const checked = fieldEl.querySelector(
            "[data-custom-field-color]:checked"
          );
          value =
            checked instanceof HTMLInputElement ? checked.value.trim() : "";
        }

        if (value) {
          values[heading] = value;
        }
      });

    return values;
  }

  function getProductOptionNames(scope) {
    return readProductConfigFromSection(getUploadSection(scope)).productOptions;
  }

  /**
   * @param {Document|Element|null|undefined} [scope]
   */
  window.getAiGenerationContext = function (scope) {
    const container = findProductContainer(scope || document);
    const form = getFormForUpload(scope || container);
    const variantPicker = container.querySelector("variant-picker");
    const variant = getSelectedVariant(variantPicker);

    const variantId = String(
      form?.querySelector('input[name="id"]')?.value || variant?.id || ""
    );

    const variantOptions = collectVariantOptionsFromData(
      scope || container,
      variant
    );

    const customProperties = collectCustomProperties(scope || container);

    return {
      variantId,
      variantTitle: String(variant?.title || variant?.name || ""),
      variantOptions,
      customProperties,
    };
  };

  function namesMatch(left, right) {
    return (
      normalizeOptionName(left).toLowerCase() ===
      normalizeOptionName(right).toLowerCase()
    );
  }

  function waitForVariantUpdate(productId, timeoutMs = 2500) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        document.removeEventListener("variant:update", onUpdate);
        clearTimeout(timer);
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      };

      const onUpdate = (event) => {
        const eventProductId = event.detail?.data?.productId;
        if (
          productId &&
          eventProductId &&
          String(eventProductId) !== String(productId)
        ) {
          return;
        }
        finish();
      };

      const timer = setTimeout(finish, timeoutMs);
      document.addEventListener("variant:update", onUpdate);
    });
  }

  function applyCustomProperties(scope, customProperties) {
    if (!customProperties || typeof customProperties !== "object") return;

    if (typeof window.ProductCustomFields?.applyValues === "function") {
      window.ProductCustomFields.applyValues(scope, customProperties);
      return;
    }

    const container = findProductContainer(scope);
    const component = container.querySelector("product-custom-fields-component");
    if (!component) return;

    Object.entries(customProperties).forEach(([heading, value]) => {
      const fieldEl = component.querySelector(
        `[data-field-heading="${CSS.escape(heading)}"]`
      );
      if (!(fieldEl instanceof HTMLElement)) return;

      const fieldType = fieldEl.getAttribute("data-field-type");
      const text = String(value ?? "").trim();

      if (fieldType === "text") {
        const input = fieldEl.querySelector("[data-custom-field-text]");
        if (input instanceof HTMLInputElement) input.value = text;
      } else if (fieldType === "select") {
        const select = fieldEl.querySelector("[data-custom-field-select]");
        if (select instanceof HTMLSelectElement) select.value = text;
      }
    });
  }

  async function applyVariantOptions(scope, context) {
    const container = findProductContainer(scope);
    const variantPicker = container.querySelector("variant-picker");
    const form = getFormForUpload(scope);
    const options = Array.isArray(context?.variantOptions)
      ? context.variantOptions
      : [];

    if (!variantPicker || !options.length) {
      if (context?.variantId && form) {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput instanceof HTMLInputElement) {
          idInput.value = String(context.variantId);
        }
      }
      return;
    }

    const optionNames = getProductOptionNames(scope);
    let changed = false;

    options.forEach(({ name, value }) => {
      if (!name || !value) return;
      const normalizedName = normalizeOptionName(name);
      const targetValue = String(value).trim();

      const select = variantPicker.querySelector(
        `select.variant-option__select[name="options[${CSS.escape(normalizedName)}]"]`
      );
      if (select instanceof HTMLSelectElement) {
        if (select.value.trim() !== targetValue) {
          select.value = targetValue;
          changed = true;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
        return;
      }

      const fieldsets = variantPicker.querySelectorAll(
        "fieldset.variant-option--buttons"
      );
      for (const fieldset of fieldsets) {
        const fieldsetIndex = Number.parseInt(
          fieldset.getAttribute("data-fieldset-index") || "",
          10
        );
        const fieldsetName =
          optionNames[fieldsetIndex] ||
          inferOptionNameFromPicker(variantPicker, fieldsetIndex);

        if (
          fieldsetName &&
          !namesMatch(fieldsetName, normalizedName)
        ) {
          continue;
        }

        if (!fieldsetName) {
          const legend = fieldset.querySelector(".variant-option__legend-label");
          const legendName = (
            legend?.childNodes[0]?.textContent ||
            legend?.textContent ||
            ""
          ).trim();
          if (!namesMatch(legendName, normalizedName)) {
            continue;
          }
        }

        const radios = fieldset.querySelectorAll('input[type="radio"]');
        for (const radio of radios) {
          if (!(radio instanceof HTMLInputElement)) continue;
          if (radio.value.trim() !== targetValue) continue;
          if (!radio.checked) {
            radio.checked = true;
            changed = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
          }
          return;
        }
      }
    });

    if (context?.variantId && form) {
      const idInput = form.querySelector('input[name="id"]');
      if (
        idInput instanceof HTMLInputElement &&
        idInput.value !== String(context.variantId)
      ) {
        idInput.value = String(context.variantId);
      }
    }

    if (changed) {
      const uploadSection = getUploadSection(scope);
      const productId = uploadSection?.dataset.productId || "";
      await waitForVariantUpdate(productId);
    }
  }

  /**
   * Restore variant + custom field values saved with a generation history entry.
   *
   * @param {Document|Element|null|undefined} scope
   * @param {{
   *   variantId?: string,
   *   variantTitle?: string,
   *   variantOptions?: { name: string, value: string }[],
   *   customProperties?: Record<string, string>,
   * } | null | undefined} context
   */
  window.applyAiGenerationContext = async function (scope, context) {
    if (!context) return;

    applyCustomProperties(scope, context.customProperties || {});

    if (
      (Array.isArray(context.variantOptions) && context.variantOptions.length) ||
      context.variantId
    ) {
      await applyVariantOptions(scope, context);
    }
  };
})();
